import { Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';

export interface SearchControlStateModel {
  nodeSearchArgs: FlatModels.SearchControlModels.NodeSearch | undefined;
  nodeResult: { total: number; items: Record<string, FlatModels.SearchControlModels.NodeData>; };
  currentSelectedNodeResult: string | null;

  mediaSearchArgs: FlatModels.SearchControlModels.MediaSearch | undefined;
  mediaResult: { total: number; items: Record<string, FlatModels.SearchControlModels.MediaData>; };
  currentSelectedMediaResult: FlatModels.SearchControlModels.MediaData | null;

  detachedPageSearchArgs: FlatModels.SearchControlModels.NodeSearch | undefined;
  detachedPageResult: { total: number; items: Record<string, FlatModels.SearchControlModels.DetachedPageData>; };
  currentSelectedDetachedPageResult: string | null;
  
}

@State<SearchControlStateModel>({
  name: 'searchControl',
  defaults: {
    nodeSearchArgs: {
      searchString: '',
      typeFilter: [],
      createdByMe: false,
      usedInFilter: undefined,
      sortField: '',
      sortOrder: 0,
      rows: 10,
      first: 0
    },
    nodeResult: { total: 0, items: {} },
    currentSelectedNodeResult: null,
    mediaSearchArgs: {
      searchString: '',
      typeFilter: [],
      uploadedByMe: false,
      usedInFilter: undefined,
      sortField: undefined,
      sortOrder: 0,
      rows: 10,
      first: 0
    },
    mediaResult: { total: 0, items: {} },
    currentSelectedMediaResult: null,
    detachedPageSearchArgs: {
      searchString: '',
      typeFilter: [],
      createdByMe: false,
      usedInFilter: undefined,
      sortField: '',
      sortOrder: 0,
      rows: 10,
      first: 0
    },
    detachedPageResult: { total: 0, items: {} },
    currentSelectedDetachedPageResult: null,
  }
})
@Injectable()
export class SearchControlState {
  public constructor(public store: Store,
                     public apiControlService: ApiControlService) {
  }

  //<editor-fold desc="Selectors">

  @Selector([SearchControlState])
  public static nodeSearchResult$(state: SearchControlStateModel) {
    return {
      total: state.nodeResult.total,
      items: Object.values(state.nodeResult.items)
    }
  }
  
  @Selector([SearchControlState])
  public static mediaResult$(state: SearchControlStateModel) {
    return state.mediaResult;
  }

  @Selector([SearchControlState])
  public static mediaSearchResult$(state: SearchControlStateModel) {
    return {
      total: state.mediaResult.total,
      items: Object.values(state.mediaResult.items)
    }
  }

  @Selector([SearchControlState])
  public static detachedPageSearchResult$(state: SearchControlStateModel) {
    return {
      total: state.detachedPageResult.total,
      items: Object.values(state.detachedPageResult.items)
    }
  }

  @Selector([SearchControlState.detachedPageSearchResult$])
  public static detachedPagesSet$(detachedPageResult: ReturnType<typeof SearchControlState.detachedPageSearchResult$>) {
    return new Set(detachedPageResult.items.map(item => item.id))
  }

  @Selector([SearchControlState])
  public static currentSelectedMediaResult$(state: SearchControlStateModel) {
    return state.currentSelectedMediaResult;
  }
  
  @Selector([
    SearchControlState.currentSelectedMediaResult$,
    FlatStates.MediaEntityState.typedEntities$
  ])
  public static currentSelectedMedia$(currentSelectedMediaResult: FlatModels.SearchControlModels.MediaData,
                                      typedMedias: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType }) {
    return currentSelectedMediaResult && typedMedias[currentSelectedMediaResult.id] || null;
  }
  
  @Selector([SearchControlState])
  public static currentSelectedNodeResult$(state: SearchControlStateModel) {
    return state.currentSelectedNodeResult;
  }
  
  @Selector([SearchControlState.currentSelectedNodeResult$])
  public static currentSelectedNode$(currentSelectedNodeResult: string) {
    return currentSelectedNodeResult || null;
  }
  //</editor-fold>

  //<editor-fold desc="Actions">
  @Action(FlatActions.SearchControlActions.FindNodes)
  public findNodes({getState, patchState}: StateContext<SearchControlStateModel>,
                   {search}: FlatActions.SearchControlActions.FindNodes) {
    if(!search && !getState().nodeSearchArgs) {
      return null;
    }

    if(search){
      patchState({
        nodeSearchArgs: search
      })
    }

    return this.apiControlService.sendMessage(
      new FlatActions.SearchControlActions.FindNodesRequest(search ?? getState().nodeSearchArgs!)
    );
  }


  @Action(FlatActions.SearchControlActions.FindMedia)
  public findMedia({getState, patchState}: StateContext<SearchControlStateModel>,
                   {search}: FlatActions.SearchControlActions.FindMedia) {

    if(!search && !getState().mediaSearchArgs) {
      return null;
    }

    if(search){
      patchState({
        mediaSearchArgs: search
      })
    }

    return this.apiControlService.sendMessage(
      new FlatActions.SearchControlActions.FindMediaRequest(search ?? getState().mediaSearchArgs!)
    );
  }

  @Action(FlatActions.SearchControlActions.FindDetachedPages)
  public findDetachedPages({getState, patchState}: StateContext<SearchControlStateModel>,
                   {search}: FlatActions.SearchControlActions.FindDetachedPages) {

    if(!search && !getState().detachedPageSearchArgs) {
      return null;
    }

    if(search){
      patchState({
        detachedPageSearchArgs: search
      })
    }

    return this.apiControlService.sendMessage(
      new FlatActions.SearchControlActions.FindDetachedPagesRequest(search ?? getState().detachedPageSearchArgs!)
    );
  }
  
  @Action(FlatActions.SearchControlActions.FindNodesSuccess)
  public findNodesSuccess({patchState, dispatch}: StateContext<SearchControlStateModel>,
                          {searchResults, totalPages}: FlatActions.SearchControlActions.FindNodesSuccess): void {
    patchState({
      nodeResult: {
        items: searchResults.reduce<Record<string, FlatModels.SearchControlModels.NodeData>>((acc, val) => {
          acc[val.id] = val;
          return acc;
        }, {}),
        total: totalPages || 0
      }
    });
    dispatch(new FlatActions.SearchControlActions.FindSuccess());
  }
  
  @Action(FlatActions.SearchControlActions.FindMediaSuccess)
  public findMediaSuccess({patchState, dispatch}: StateContext<SearchControlStateModel>,
                          {searchResults, totalMedia}: FlatActions.SearchControlActions.FindMediaSuccess): void {
    patchState({
      mediaResult: {
        items: searchResults.reduce<Record<string, FlatModels.SearchControlModels.MediaData>>((acc, val) => {
          acc[val.id] = val;
          return acc;
        }, {}),
        total: totalMedia || 0
      }
    });
    dispatch(new FlatActions.SearchControlActions.FindSuccess());
  }

  @Action(FlatActions.SearchControlActions.FindDetachedPagesSuccess)
  public findDetachedPagesSuccess({patchState, dispatch}: StateContext<SearchControlStateModel>,
                          {searchResults, totalPages}: FlatActions.SearchControlActions.FindDetachedPagesSuccess): void {
    patchState({
      detachedPageResult: {
        items: searchResults.reduce<Record<string, FlatModels.SearchControlModels.DetachedPageData>>((acc, val) => {
          acc[val.id] = val;
          return acc;
        }, {}),
        total: totalPages || 0
      }
    });
    dispatch(new FlatActions.SearchControlActions.FindSuccess());
  }
  
  @Action(FlatActions.SearchControlActions.SetCurrentSelectedMediaResult)
  public setCurrentSelectedMediaResult({patchState}: StateContext<SearchControlStateModel>,
                                       {currentSelectedMediaResult}: FlatActions.SearchControlActions.SetCurrentSelectedMediaResult) {
    patchState({
      currentSelectedMediaResult
    });
  }
  
  @Action(FlatActions.SearchControlActions.SetCurrentSelectedNodeResult)
  public setCurrentSelectedSearchResult({patchState}: StateContext<SearchControlStateModel>,
                                        {currentSelectedNodeResult}: FlatActions.SearchControlActions.SetCurrentSelectedNodeResult) {
    patchState({
      currentSelectedNodeResult
    });
  }
  
  //</editor-fold>
}
